import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { CallMediaPermissionValue, IDeviceInfo } from '@gem/call/CallStore/Call.types';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import {
  ICallPersonalizedPermissions,
  PermissionsPersonalizedValue,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { useTranslation } from '@gem/modules/i18n';
import { popupsTasks } from '@gem/popups';

interface ICallVideoButtonPresenter {
  ongoingCall: Call;
  cameraPermissions: PermissionsPersonalizedValue | null;
  isVideoMuted: boolean;
  isCallStarted: boolean;
  isMeInitiatorOwnerOrAdmin: boolean;
  isVideoPopoverOpened: boolean;
  permissions: ICallPersonalizedPermissions | null;
  devices: IDeviceInfo[];
  currentDeviceId: string | null;
  canUseBlur: boolean;
  videoPermission: CallMediaPermissionValue;
  setCurrentDeviceId(deviceId: string): void;
  closeVideoPopover(): void;
  onVideoArrowClick(): void;
  unmuteVideo(): Promise<void>;
  showBackgroundPanel(): void;
  onVideoPopoverOpen?(): void;
  muteVideo(): void;
}

interface ICallVideoButtonPresenterProps {
  checkAndSendPermissionRequest(permissionType: PermissionsTypes): Promise<boolean>;
  onVideoPopoverOpen?(): void;
  permissions: ICallPersonalizedPermissions | null;
  videoPermission: CallMediaPermissionValue;
}

export const useCallVideoButtonPresenter = (
  props: ICallVideoButtonPresenterProps
): ICallVideoButtonPresenter => {
  const context = useCallContext();
  const { t } = useTranslation();

  const presenter = useLocalObservable<ICallVideoButtonPresenter>(() => ({
    isVideoPopoverOpened: false,
    permissions: props.permissions,
    videoPermission: CallMediaPermissionValue.Initial,
    onVideoPopoverOpen: props.onVideoPopoverOpen,

    get ongoingCall(): Call {
      return context.call;
    },

    get canUseBlur(): boolean {
      return context.callController.canUseBlur;
    },

    get devices(): IDeviceInfo[] {
      return presenter.ongoingCall.mediaDevices.devices.videoinput;
    },

    get currentDeviceId(): string | null {
      return presenter.ongoingCall.mediaDevices.currentDeviceIds.videoinput;
    },

    get cameraPermissions(): PermissionsPersonalizedValue | null {
      return presenter.permissions && presenter.permissions[PermissionsTypes.SwitchOnCamera]
        ? presenter.permissions[PermissionsTypes.SwitchOnCamera]
        : null;
    },

    get isVideoMuted(): boolean {
      return presenter.ongoingCall.meStore.isVideoMuted;
    },

    get isCallStarted(): boolean {
      return presenter.ongoingCall.isStarted;
    },

    get isMeInitiatorOwnerOrAdmin(): boolean {
      return presenter.ongoingCall.isMeInitiatorOwnerOrAdmin;
    },

    setCurrentDeviceId(deviceId: string): void {
      context.callController.setCurrentDeviceId('videoinput', deviceId);
    },

    muteVideo(): void {
      context.callController.muteVideo();
    },

    async unmuteVideo(): Promise<void> {
      const canUnmuteVideo = await props.checkAndSendPermissionRequest(
        PermissionsTypes.SwitchOnCamera
      );

      if (!canUnmuteVideo) {
        return;
      }
      if (presenter.ongoingCall.meStore.isVideoMuted && !presenter.ongoingCall.canUnmute) {
        popupsTasks.showAlert(t('premium_extended_calls_broadcasters_limit_exceeded'));
        return;
      }

      await context.callController.unmuteVideo(true);
    },

    closeVideoPopover(): void {
      presenter.isVideoPopoverOpened = false;
    },

    async onVideoArrowClick(): Promise<void> {
      if (presenter.videoPermission === CallMediaPermissionValue.Initial) {
        const hasCameraPermission = await context.callController.checkCameraPermission(true);

        if (hasCameraPermission) {
          presenter.onVideoPopoverOpen && presenter.onVideoPopoverOpen();
          presenter.isVideoPopoverOpened = true;
        }
      } else {
        presenter.onVideoPopoverOpen && presenter.onVideoPopoverOpen();
        presenter.isVideoPopoverOpened = true;
      }
    },

    showBackgroundPanel(): void {
      if (presenter.ongoingCall.meStore.isVideoMuted) {
        popupsTasks.showAlert(t('call_background_camera_is_off_message'));

        return;
      }

      if (presenter.ongoingCall.viewState.isMembersShown) {
        presenter.ongoingCall.viewState.hideMembers();
      }
      if (presenter.ongoingCall.viewState.isMinimized) {
        presenter.ongoingCall.viewState.expand();
      }

      presenter.ongoingCall.viewState.toggleBackgroundPanel();
    },
  }));

  useEffect(() => {
    presenter.onVideoPopoverOpen = props.onVideoPopoverOpen;
  }, [props.onVideoPopoverOpen]);

  useEffect(() => {
    presenter.permissions = props.permissions;
    presenter.videoPermission = props.videoPermission;
  }, [props.permissions, props.videoPermission]);

  useEffect(() => {
    if (
      !presenter.isVideoMuted &&
      presenter.cameraPermissions !== PermissionsPersonalizedValue.Always &&
      !presenter.isMeInitiatorOwnerOrAdmin
    ) {
      presenter.muteVideo();
    }
  }, [presenter.isVideoMuted, presenter.cameraPermissions, presenter.isMeInitiatorOwnerOrAdmin]);

  return presenter;
};
